(() => {
  if (!customElements.get('cancha-pdp-tabs')) {
    customElements.define('cancha-pdp-tabs', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        this.addEventListener('click', event => {
          const tab = event.target.closest('[role="tab"]');
          if (tab) this.select(tab);
        }, options);
        this.addEventListener('keydown', event => {
          const tabs = [...this.querySelectorAll('[role="tab"]')];
          const index = tabs.indexOf(event.target);
          if (index < 0) return;
          let next;
          if (['ArrowDown', 'ArrowRight'].includes(event.key)) next = (index + 1) % tabs.length;
          if (['ArrowUp', 'ArrowLeft'].includes(event.key)) next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(tabs[next]);
          tabs[next].focus();
        }, options);
        document.addEventListener('shopify:block:select', event => {
          const tab = event.target.closest('[role="tab"]');
          if (tab && this.contains(tab)) this.select(tab);
        }, options);
      }
      select(selected) {
        this.querySelectorAll('[role="tab"]').forEach(tab => {
          const active = tab === selected;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) {
            panel.hidden = !active;
            if (!active) panel.querySelectorAll('video').forEach(video => video.pause());
          }
        });
      }
      disconnectedCallback() { this.abort?.abort(); }
    });
  }

  if (!customElements.get('cancha-comparison-action')) {
    customElements.define('cancha-comparison-action', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        this.addEventListener('click', async event => {
          const link = event.target.closest('[data-comparison-quiz]');
          if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const drawer = document.getElementById('cancha-assistant-drawer');
          if (!drawer || !window.loadCanchaAIChat) return;
          event.preventDefault();
          if (this.opening) return;
          this.opening = true;
          let timeout;
          try {
            window.loadCanchaAIChat();
            await Promise.race([
              Promise.all([customElements.whenDefined('ai-chat-widget'), customElements.whenDefined('x-drawer')]),
              new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Assistant unavailable')), 8000); })
            ]);
            if (!this.isConnected) return;
            const quiz = drawer.querySelector('[data-assistant-action="quiz"]');
            if (!quiz || !drawer.show) throw new Error('Quiz unavailable');
            await drawer.show();
            quiz.click();
          } catch {
            if (this.isConnected) window.location.assign(link.href);
          } finally {
            clearTimeout(timeout);
            this.opening = false;
          }
        }, { signal: this.abort.signal });
      }
      disconnectedCallback() { this.abort?.abort(); }
    });
  }

  if (customElements.get('cancha-pdp-controller')) return;
  customElements.define('cancha-pdp-controller', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.schedule = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updatePurchase(); });
      };
      ['bundle-switcher:change', 'variant:change', 'product:rerender', 'shopify:section:load'].forEach(name => document.addEventListener(name, this.schedule, { ...options, capture: true }));
      document.addEventListener('change', this.schedule, options);
      document.addEventListener('click', event => {
        const preset = event.target.closest('[data-cp-preset]');
        if (preset) {
          const builder = document.querySelector('[data-cp-builder]');
          const option = builder?.querySelector('[data-cp-setup="' + preset.dataset.cpPreset + '"]');
          if (option) { event.preventDefault(); builder.scrollIntoView({ behavior: 'instant' }); option.click(); }
        }
      }, options);
      let scrolling = false;
      this.onScroll = () => {
        if (scrolling) return;
        scrolling = true;
        requestAnimationFrame(() => { scrolling = false; if (this.isConnected) this.updateStickyCart(); });
      };
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      window.addEventListener('resize', this.onScroll, options);
      this.updateStickyCart();
      this.schedule();
      this.observer = new MutationObserver(this.schedule);
      const builder = document.querySelector('[data-cp-builder]');
      if (builder) this.observer.observe(builder, { childList: true, subtree: true });
    }
    setText(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }
    updateStickyCart() {
      const form = document.querySelector('[data-cp-builder] product-form form');
      const button = form?.querySelector('button[type="submit"]');
      const bar = [...document.querySelectorAll('product-sticky-add-to-cart')].find(element => element.getAttribute('main-product-form') === form?.getAttribute('id'));
      if (!button || !bar) return;
      const footer = document.querySelector('.shopify-section--footer');
      const pastPurchase = button.getBoundingClientRect().bottom <= 0;
      const beforeFooter = !footer || footer.getBoundingClientRect().top >= window.innerHeight;
      bar.classList.toggle('is-visible', pastPurchase && beforeFooter);
    }
    updatePurchase() {
      const builder = document.querySelector('[data-cp-builder]');
      const form = builder?.querySelector('product-form form');
      const variantData = builder?.querySelector('variant-picker script[data-variant]');
      if (!form || !variantData) return;
      let variant;
      try { variant = JSON.parse(variantData.textContent); } catch { return; }
      if (!variant || typeof variant.price !== 'number') return;
      const priceLabel = this.money.format(variant.price / 100);
      const selectedOption = builder.querySelector('[data-bundle-current]');
      const setup = selectedOption?.dataset.cpTitle || this.dataset.bagLabel;
      const selection = [setup, variant.title === 'Default Title' ? '' : variant.title].filter(Boolean).join(' · ');
      document.querySelectorAll('[data-cp-total]').forEach(element => this.setText(element, priceLabel));
      document.querySelectorAll('[data-cp-selection]').forEach(element => this.setText(element, selection));
      const mainButton = form.querySelector('button[type="submit"]');
      document.querySelectorAll('[data-cp-submit]').forEach(button => {
        button.hidden = false;
        button.setAttribute('form', form.getAttribute('id'));
        button.disabled = !mainButton || mainButton.disabled;
        this.setText(button, mainButton?.textContent.trim() || this.dataset.cartLabel);
      });
      const dispatch = builder.querySelector('delivery-promise')?.textContent.trim().replace(/\s+/g, ' ');
      if (dispatch) document.querySelectorAll('[data-cp-dispatch]').forEach(element => this.setText(element, dispatch));
      this.updateStickyCart();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
})();
